import React, { useLayoutEffect } from 'react';
import TableHeader from './TableHeader';
import TableBody from './TableBody';
import TableFooter from './Footer/TableFooter';
import { getTableClass, isDeviceColumn, isGeoColumn } from '../../../../util/table-helper';
import { Opacity } from '../../../../enum/Opacity';
import ViewPerCurrency from './Footer/ViewPerCurrency';
import ExpandSum from './Footer/ExpandSum';
import { useAppSelector } from '../../../../hook/useAppSelector';
import { useColumnDragDrop } from '../../../../hook/useColumnDragDrop';
import { Column } from '../../../../enum/Column';
import { t } from 'i18next';
import { VirtualItem } from '@tanstack/react-virtual';
import upsideDownArrow from '../../../../../src/assets/img/png/miscellaneous/upside-down-arrow.png';
import arrowDownWhite from '../../../../../src/assets/img/png/miscellaneous/arrow-down-white.png';
import arrowUpWhite from '../../../../../src/assets/img/png/miscellaneous/arrow-up-white.png';
import { SortDirection } from '../../../../enum/SortDirection';
import { usePageVisibleColumns } from '../../../../hook/usePageVisibleColumns';
import { useNumberColumnWidth } from '../../../../hook/useNumberColumnWidth';
import { useViewPerCurrency } from '../../../../hook/useViewPerCurrency';

interface TableColumnProps {
  column: Column;
  data: Record<string, number>[];
  isFirstColumnAfterCCY: boolean;
  isLastColumn: boolean;
  showFooter: boolean;
  className?: string;
  selectedRow: number | null;
  onRowSelect: (rowIndex: number | null) => void;
  virtualRows: VirtualItem[];
  totalHeight: number;
  visibleRowsHeight: number;
  toggleSort: (key: string) => void;
  sortConfig: { key: string; direction: SortDirection } | null;
}

const EMPTY_DATA: Record<string, number>[] = [];
const EMPTY_FOOTER_VALUES: (string | number)[] = [];

const TableColumn: React.FC<TableColumnProps> = ({
  column,
  data,
  isFirstColumnAfterCCY,
  isLastColumn,
  showFooter,
  className,
  selectedRow,
  onRowSelect,
  virtualRows,
  totalHeight,
  visibleRowsHeight,
  toggleSort,
  sortConfig,
}) => {
  const shouldViewPerCurrency = useAppSelector((state) => state.data.shouldViewPerCurrency);
  const shouldExpandSum = useAppSelector((state) => state.data.shouldExpandSum);
  const selectedCurrency = useAppSelector((state) => state.configSlice.selectedCurrency);
  const { visibleColumns } = usePageVisibleColumns();

  const { ref, isDragging } = useColumnDragDrop({
    column,
  });
  const isMobile = useAppSelector((state) => state.configSlice.isMobile);

  const isNumberColumn = className === 'number';
  // Same conditions as setFooter: only ViewPerCurrency / ExpandSum render sums.
  const isSumFooterVisible =
    showFooter &&
    ((shouldViewPerCurrency && !shouldExpandSum) ||
      ((shouldExpandSum || selectedCurrency !== null) && !shouldViewPerCurrency));
  const footerValues = useViewPerCurrency(column.name);
  const numberColumnWidth = useNumberColumnWidth(
    column.name,
    isNumberColumn ? data : EMPTY_DATA,
    isNumberColumn && isSumFooterVisible ? footerValues : EMPTY_FOOTER_VALUES,
  );

  const getSortIcon = () => {
    if (!sortConfig || sortConfig.key !== column.name) {
      return upsideDownArrow;
    }
    return sortConfig.direction === SortDirection.ASCENDING ? arrowUpWhite : arrowDownWhite;
  };

  useLayoutEffect(() => {
    const updateStickyOffsets = () => {
      const columns = !isMobile
        ? document.querySelectorAll('.table_column.sticky_column')
        : document.querySelectorAll('.table_column.pinned');
      let offset = 0;

      columns.forEach((col) => {
        const el = col as HTMLElement;
        el.style.left = `${offset}px`;
        offset += el.offsetWidth;
      });
    };

    updateStickyOffsets();
    window.addEventListener('resize', updateStickyOffsets);

    return () => window.removeEventListener('resize', updateStickyOffsets);
  });

  const setFooter = () => {
    if (shouldViewPerCurrency && !shouldExpandSum) {
      return (
        <ViewPerCurrency
          column={column.name}
          isLastColumn={isLastColumn}
          className="visible_footer"
          visibleFooter={true}
        />
      );
    }

    if ((shouldExpandSum || selectedCurrency !== null) && !shouldViewPerCurrency) {
      return (
        <ExpandSum
          column={column.name}
          isFirstColumnAfterCCY={isFirstColumnAfterCCY}
          isLastColumn={isLastColumn}
          className="visible_footer"
        />
      );
    }

    return (
      <TableFooter column={column.name} isFirstColumnAfterCCY={isFirstColumnAfterCCY} isLastColumn={isLastColumn} />
    );
  };

  return (
    <div
      style={{
        opacity: isDragging ? Opacity.DRAGGING : Opacity.DEFAULT,
        ...(isNumberColumn ? { width: numberColumnWidth, minWidth: numberColumnWidth } : {}),
      }}
      className={`table_column ${getTableClass(column.name, 'column', visibleColumns, isMobile)} ${`${className}_column`}
       ${isGeoColumn(column.name) ? 'geo' : ''} ${isDeviceColumn(column.name) ? 'device' : ''}`}
    >
      <div ref={column.isLocked ? null : ref} className="table_header_wrapper">
        <TableHeader
          column={t(`columns.${column.name}`)}
          columnKey={column.name}
          toggleSort={toggleSort}
          getSort={getSortIcon}
        />
      </div>
      <div
        style={{
          height: totalHeight,
          position: 'relative',
          width: '100%',
        }}
        className="virtual_body virtual"
        data-pw={`column${column.name}`}
      >
        <TableBody
          column={column.name}
          data={data}
          selectedRow={selectedRow}
          onRowSelect={onRowSelect}
          virtualRows={virtualRows}
          visibleRowsHeight={visibleRowsHeight}
        />
      </div>
      {showFooter && setFooter()}
    </div>
  );
};

export default TableColumn;
